import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "./ui/sonner";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Wand2, Rocket, Link2, Loader2, CheckCircle2, XCircle, ShieldCheck, Image, Inbox,
  BarChart3, TrendingUp, MousePointerClick, Eye, Users, KeyRound, UserPlus, Search, Disc3 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const AStat = ({ label, value, Icon }) => (
  <div className="rounded-md border border-white/10 bg-[#141417] p-5">
    <div className="flex items-center justify-between">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</div>
      <Icon className="h-4 w-4 text-cyan-400" />
    </div>
    <div className="mt-1 font-heading text-3xl font-black">{value ?? "—"}</div>
  </div>
);

const BarList = ({ items, labelKey = "query", valueKey = "count", testId, empty = "No data yet" }) => {
  const max = Math.max(1, ...items.map((i) => i[valueKey]));
  return (
    <div data-testid={testId} className="space-y-2.5">
      {items.length === 0 && <p className="font-mono text-xs uppercase tracking-widest text-white/25">{empty}</p>}
      {items.map((it, i) => (
        <div key={i}>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="truncate text-white/80">{it[labelKey]}</span>
            <span className="shrink-0 font-mono text-xs text-cyan-300">{it[valueKey]}</span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/5">
            <div className="h-full rounded-full bg-cyan-500/70" style={{ width: `${(it[valueKey] / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    fetch(`${API}/api/admin/analytics`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const maxDay = data ? Math.max(1, ...data.searches_over_time.map((d) => d.count)) : 1;

  return (
    <div className="mt-8" data-testid="analytics-dashboard">
      <div className="flex items-center gap-2 font-heading text-lg font-black tracking-tight">
        <BarChart3 className="h-5 w-5 text-cyan-400" /> Usage Dashboard
      </div>

      {!data ? (
        <div className="mt-4 flex justify-center rounded-md border border-white/10 bg-[#141417] py-12">
          <Loader2 className="h-5 w-5 animate-spin text-cyan-400" />
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <AStat label="Site visits" value={data.total_visits?.toLocaleString()} Icon={Eye} />
            <AStat label="Unique visitors" value={data.unique_visitors?.toLocaleString()} Icon={Users} />
            <button data-testid="registered-users-card" onClick={() => document.getElementById("registered-members")?.scrollIntoView({ behavior: "smooth" })}
                    className="rounded-md border border-cyan-500/30 bg-[#141417] p-5 text-left transition-colors duration-200 hover:border-cyan-500/60">
              <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300/70">Registered users ↓</div>
                <Users className="h-4 w-4 text-cyan-400" />
              </div>
              <div className="mt-1 font-heading text-3xl font-black">{data.total_users?.toLocaleString() ?? "—"}</div>
            </button>
            <AStat label="New signups · 7d" value={data.signups_7d?.toLocaleString()} Icon={UserPlus} />
            <AStat label="Total searches" value={data.total_searches?.toLocaleString()} Icon={Search} />
            <AStat label="Searches · 7d" value={data.searches_7d?.toLocaleString()} Icon={TrendingUp} />
            <AStat label="Click-through rate" value={`${data.ctr}%`} Icon={MousePointerClick} />
            <AStat label="Active members · 7d" value={data.active_members?.toLocaleString()} Icon={Users} />
          </div>

          <div className="mt-4 rounded-md border border-white/10 bg-[#141417] p-6">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Searches · last 14 days</div>
              <div className="font-mono text-[10px] text-white/30">{data.total_views?.toLocaleString()} views · {data.total_clicks?.toLocaleString()} clicks</div>
            </div>
            <div className="mt-5 flex h-32 items-end gap-1.5" data-testid="searches-timeseries">
              {data.searches_over_time.map((d) => (
                <div key={d.day} className="group relative flex flex-1 flex-col items-center justify-end">
                  <div className="w-full rounded-t bg-cyan-500/60 transition-colors duration-150 group-hover:bg-cyan-400"
                       style={{ height: `${Math.max(3, (d.count / maxDay) * 100)}%` }} />
                  <span className="mt-1 font-mono text-[8px] text-white/25">{d.day.slice(5)}</span>
                  <span className="pointer-events-none absolute -top-5 rounded bg-black px-1.5 py-0.5 font-mono text-[10px] text-cyan-300 opacity-0 transition-opacity group-hover:opacity-100">{d.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-md border border-white/10 bg-[#141417] p-6">
              <div className="mb-4 flex items-center gap-2 font-heading text-sm font-bold"><Search className="h-4 w-4 text-cyan-400" /> Top search queries</div>
              <BarList items={data.top_queries} testId="top-queries" empty="No searches yet" />
            </div>
            <div className="rounded-md border border-white/10 bg-[#141417] p-6">
              <div className="mb-4 flex items-center gap-2 font-heading text-sm font-bold"><Disc3 className="h-4 w-4 text-cyan-400" /> Top Sound-Match records</div>
              <BarList items={data.top_matches} testId="top-matches" empty="No matches yet" />
            </div>
            <div className="rounded-md border border-white/10 bg-[#141417] p-6">
              <div className="mb-4 flex items-center gap-2 font-heading text-sm font-bold"><MousePointerClick className="h-4 w-4 text-cyan-400" /> Click-through by website</div>
              <BarList items={data.vendors} labelKey="name" testId="vendor-ctr" empty="No clicks yet" />
            </div>
            <div className="rounded-md border border-white/10 bg-[#141417] p-6">
              <div className="mb-4 flex items-center gap-2 font-heading text-sm font-bold"><Eye className="h-4 w-4 text-cyan-400" /> Most-viewed plugins</div>
              <BarList items={data.most_viewed} labelKey="name" testId="most-viewed" empty="No views yet" />
            </div>
            <div className="rounded-md border border-white/10 bg-[#141417] p-6 lg:col-span-2">
              <div className="mb-4 flex items-center gap-2 font-heading text-sm font-bold"><MousePointerClick className="h-4 w-4 text-cyan-400" /> Most-clicked plugins</div>
              <BarList items={data.most_clicked} labelKey="name" testId="most-clicked" empty="No clicks yet" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const AdminSettings = () => {
  const [admins, setAdmins] = useState([]);
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [nu, setNu] = useState("");
  const [ne, setNe] = useState("");
  const [nap, setNap] = useState("");
  const [addBusy, setAddBusy] = useState(false);

  const loadAdmins = useCallback(() => {
    fetch(`${API}/api/auth/admin/admins`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setAdmins)
      .catch(() => setAdmins([]));
  }, []);
  useEffect(() => { loadAdmins(); }, [loadAdmins]);

  const changePassword = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      const res = await fetch(`${API}/api/auth/admin/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ current_password: cur, new_password: np }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      toast.success("Password updated");
      setCur(""); setNp("");
    } catch (err) {
      toast.error(String(err.message));
    } finally {
      setPwBusy(false);
    }
  };

  const addAdmin = async (e) => {
    e.preventDefault();
    setAddBusy(true);
    try {
      const res = await fetch(`${API}/api/auth/admin/add-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username: nu, email: ne, password: nap }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      toast.success(`Admin @${nu} created`);
      setNu(""); setNe(""); setNap("");
      loadAdmins();
    } catch (err) {
      toast.error(String(err.message));
    } finally {
      setAddBusy(false);
    }
  };

  const inputCls = "w-full rounded-md border border-white/10 bg-black/40 px-3.5 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-cyan-500/50";

  return (
    <div className="mt-8" data-testid="admin-settings">
      <div className="flex items-center gap-2 font-heading text-lg font-black tracking-tight">
        <KeyRound className="h-5 w-5 text-cyan-400" /> Admin Settings
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-md border border-white/10 bg-[#141417] p-6">
          <div className="font-heading text-base font-bold">Change your password</div>
          <p className="mt-1.5 text-sm text-white/50">Use at least 12 characters.</p>
          <form onSubmit={changePassword} className="mt-4 space-y-3">
            <input data-testid="cur-password-input" type="password" value={cur} onChange={(e) => setCur(e.target.value)}
                   placeholder="Current password" required className={inputCls} />
            <input data-testid="new-password-input" type="password" value={np} onChange={(e) => setNp(e.target.value)}
                   placeholder="New password (min 12 chars)" required minLength={12} className={inputCls} />
            <button data-testid="change-password-btn" disabled={pwBusy}
                    className="rounded-lg bg-cyan-500 px-5 py-2 text-sm font-semibold text-black transition-colors duration-200 hover:bg-cyan-400 disabled:opacity-40">
              {pwBusy ? "Updating..." : "Update password"}
            </button>
          </form>
        </div>

        <div className="rounded-md border border-white/10 bg-[#141417] p-6">
          <div className="flex items-center gap-2 font-heading text-base font-bold"><UserPlus className="h-4 w-4 text-cyan-400" /> Add an admin</div>
          <p className="mt-1.5 text-sm text-white/50">Creates a new account with full admin access.</p>
          <form onSubmit={addAdmin} className="mt-4 space-y-3">
            <input data-testid="new-admin-username" value={nu} onChange={(e) => setNu(e.target.value)}
                   placeholder="Username" required className={inputCls} />
            <input data-testid="new-admin-email" type="email" value={ne} onChange={(e) => setNe(e.target.value)}
                   placeholder="Email" required className={inputCls} />
            <input data-testid="new-admin-password" type="password" value={nap} onChange={(e) => setNap(e.target.value)}
                   placeholder="Password (min 12 chars)" required minLength={12} className={inputCls} />
            <button data-testid="add-admin-btn" disabled={addBusy}
                    className="rounded-lg bg-cyan-500 px-5 py-2 text-sm font-semibold text-black transition-colors duration-200 hover:bg-cyan-400 disabled:opacity-40">
              {addBusy ? "Creating..." : "Create admin"}
            </button>
          </form>
        </div>
      </div>

      <div className="mt-4 rounded-md border border-white/10 bg-[#141417] p-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Current admins ({admins.length})</div>
        <div className="mt-3 space-y-2" data-testid="admins-list">
          {admins.map((a) => (
            <div key={a.user_id} className="flex items-center gap-3 rounded-md border border-white/10 px-4 py-2.5">
              <ShieldCheck className="h-4 w-4 text-cyan-400" />
              <span className="text-sm font-semibold">@{a.username}</span>
              <span className="text-xs text-white/40">{a.email}</span>
              {a.is_you && <span className="rounded bg-cyan-500/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-cyan-300">You</span>}
              <span className="ml-auto font-mono text-[10px] text-white/30">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const FeedbackInbox = () => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    fetch(`${API}/api/admin/feedback`, { credentials: "include" })
      .then((r) => r.json())
      .then(setItems)
      .catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await fetch(`${API}/api/admin/feedback/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status }),
    });
    load();
  };

  return (
    <div className="mt-8 rounded-md border border-white/10 bg-[#141417] p-6">
      <div className="flex items-center gap-2 font-heading text-base font-bold">
        <Inbox className="h-4 w-4 text-cyan-400" /> Feedback Inbox
      </div>
      <p className="mt-1.5 text-sm text-white/50">Plugin issues and catalog suggestions from the community.</p>
      <div className="mt-4 space-y-2">
        {items === null && <Loader2 className="h-4 w-4 animate-spin text-cyan-400" />}
        {items !== null && items.length === 0 && (
          <p className="font-mono text-xs uppercase tracking-widest text-white/25">Inbox zero — nothing yet</p>
        )}
        {(items || []).map((f) => (
          <div key={f.id} data-testid="feedback-item"
               className={`rounded-md border px-4 py-3 ${f.status === "new" ? "border-cyan-500/25 bg-cyan-500/5" : "border-white/10"}`}>
            <div className="flex items-center gap-2">
              <span className={`rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
                f.kind === "suggestion" ? "bg-amber-500/15 text-amber-300" : "bg-red-500/15 text-red-300"}`}>
                {f.kind === "suggestion" ? "Suggestion" : f.category || "Issue"}
              </span>
              {f.status !== "new" && (
                <span className={`rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
                  f.status === "done" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-white/50"}`}>
                  {f.status}
                </span>
              )}
              {f.plugin_name && <span className="text-sm font-semibold">{f.plugin_name}</span>}
              <span className="text-xs text-white/40">by @{f.username}</span>
              <span className="ml-auto font-mono text-[10px] text-white/30">{new Date(f.created_at).toLocaleDateString()}</span>
            </div>
            <p className="mt-1.5 text-sm text-white/70">{f.text}</p>
            {f.status === "new" && (
              <div className="mt-2 flex gap-2">
                <button data-testid="feedback-done-btn" onClick={() => setStatus(f.id, "done")}
                        className="rounded px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-emerald-400 hover:bg-emerald-500/10">
                  Mark done
                </button>
                <button data-testid="feedback-dismiss-btn" onClick={() => setStatus(f.id, "dismissed")}
                        className="rounded px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-white/40 hover:bg-white/5">
                  Dismiss
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const JobCard = ({ title, desc, job, onRun, testId, Icon }) => {
  const running = job?.status === "running";
  const pct = job?.total ? Math.round((job.done / job.total) * 100) : 0;
  return (
    <div className="rounded-md border border-white/10 bg-[#141417] p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 font-heading text-base font-bold">
            <Icon className="h-4 w-4 text-cyan-400" /> {title}
          </div>
          <p className="mt-1.5 text-sm text-white/50">{desc}</p>
        </div>
        <button
          data-testid={testId}
          disabled={running}
          onClick={onRun}
          className="shrink-0 rounded-lg bg-cyan-500 px-4 py-2 text-sm font-semibold text-black transition-colors duration-200 hover:bg-cyan-400 disabled:opacity-40"
        >
          {running ? "Running..." : "Run"}
        </button>
      </div>
      {job && (
        <div className="mt-4">
          {running && (
            <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
              <div className="h-full rounded-full bg-cyan-400 transition-[width] duration-500" style={{ width: `${pct}%` }} />
            </div>
          )}
          <div className="mt-2 flex items-center gap-2 font-mono text-[11px] text-white/50">
            {running && <Loader2 className="h-3 w-3 animate-spin text-cyan-400" />}
            {job.status === "done" && <CheckCircle2 className="h-3 w-3 text-emerald-400" />}
            {job.status === "error" && <XCircle className="h-3 w-3 text-red-400" />}
            <span>{job.message} {running && job.total ? `(${job.done}/${job.total})` : ""}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const RegisteredMembers = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    fetch(`${API}/api/admin/users`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, []);

  const users = (data?.users || []).filter((u) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (u.username || "").toLowerCase().includes(t) || (u.email || "").toLowerCase().includes(t);
  });

  return (
    <div id="registered-members" className="mt-8" data-testid="registered-members">
      <div className="flex items-center gap-2 font-heading text-lg font-black tracking-tight">
        <Users className="h-5 w-5 text-cyan-400" /> Registered Users
        {data && <span className="rounded bg-cyan-500/15 px-2 py-0.5 font-mono text-xs text-cyan-300">{data.total}</span>}
      </div>
      <div className="mt-4 rounded-md border border-white/10 bg-[#141417]">
        <div className="border-b border-white/10 p-4">
          <input
            data-testid="user-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by username or email..."
            className="w-full rounded-md border border-white/10 bg-black/40 px-3.5 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-cyan-500/50"
          />
        </div>
        {!data ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>
        ) : users.length === 0 ? (
          <p className="py-10 text-center font-mono text-xs uppercase tracking-widest text-white/25">No users found</p>
        ) : (
          <div className="divide-y divide-white/5" data-testid="users-list">
            {users.map((u, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-heading text-sm font-black text-black"
                      style={{ background: u.avatar_color || "#00F0FF" }}>
                  {(u.username || u.email || "?").charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold">{u.username || <span className="italic text-white/40">no username yet</span>}</span>
                    {u.role === "admin" && <span className="rounded bg-cyan-500/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-cyan-300">Admin</span>}
                    <span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-white/40">{u.provider === "google" ? "Google" : "Email"}</span>
                  </div>
                  <div className="truncate text-xs text-white/50">{u.email}</div>
                </div>
                <span className="shrink-0 font-mono text-[10px] text-white/30">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminPage() {
  const { user, loading: authLoading, openAuth } = useAuth();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState({});
  const [names, setNames] = useState("");
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const refresh = useCallback(async () => {
    const [s, j] = await Promise.all([
      fetch(`${API}/api/stats`).then((r) => r.json()),
      fetch(`${API}/api/admin/jobs`, { credentials: "include" }).then((r) => (r.ok ? r.json() : {})),
    ]);
    setStats(s);
    setJobs(j);
  }, []);

  useEffect(() => { if (isAdmin) refresh(); }, [refresh, isAdmin]);
  const anyRunning = ["expand", "resolve", "link_health", "images"].some((k) => jobs[k]?.status === "running");
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [anyRunning, refresh]);

  const runJob = async (path) => {
    const res = await fetch(`${API}/api/admin/${path}`, { method: "POST", credentials: "include" });
    if (res.ok) { toast.success("Job started"); refresh(); }
    else toast.error((await res.json()).detail || "Failed to start job");
  };

  const doPreview = async () => {
    const list = names.split("\n").map((n) => n.trim()).filter(Boolean);
    if (!list.length) return toast.error("Enter at least one plugin name");
    setBusy(true);
    setPreview(null);
    try {
      const res = await fetch(`${API}/api/admin/import/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ names: list }),
      });
      if (!res.ok) throw new Error("Preview failed");
      const data = await res.json();
      setPreview(data.entries);
      setSelected(Object.fromEntries(data.entries.map((_, i) => [i, true])));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    const docs = preview.filter((_, i) => selected[i]).map((e) => e.doc);
    if (!docs.length) return toast.error("Nothing selected");
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/import/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ docs }),
      });
      const data = await res.json();
      if (data.added > 0) {
        toast.success(`Added ${data.added} plugins${data.skipped ? `, skipped ${data.skipped} duplicates` : ""}`);
      } else {
        toast.warning(`Nothing added — ${data.skipped} already in the catalog`);
      }
      setPreview(null);
      setNames("");
      refresh();
    } catch {
      toast.error("Import failed");
    } finally {
      setBusy(false);
    }
  };

  if (authLoading || !isAdmin) {
    return (
      <div className="grain flex min-h-screen items-center justify-center bg-[#09090B] text-white">
        {authLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
        ) : (
          <div data-testid="admin-gate" className="text-center">
            <p className="font-heading text-xl font-bold">Admin access required</p>
            <p className="mt-2 text-sm text-white/50">Sign in with an admin account to manage the catalog.</p>
            <div className="mt-5 flex justify-center gap-3">
              <button data-testid="admin-gate-signin" onClick={openAuth}
                      className="rounded-lg bg-cyan-500 px-5 py-2 text-sm font-semibold text-black transition-colors duration-200 hover:bg-cyan-400">
                Sign in
              </button>
              <Link to="/" className="rounded-lg border border-white/15 px-5 py-2 text-sm text-white/70 transition-colors duration-200 hover:text-white">
                Back to search
              </Link>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="grain min-h-screen bg-[#09090B] text-white">
      <Toaster theme="dark" position="bottom-right" />
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link data-testid="back-home-link" to="/" className="flex items-center gap-2 text-sm text-white/60 transition-colors duration-200 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to search
          </Link>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">Catalog Admin</span>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-5xl px-6 pb-24 pt-10">
        <div className="grid grid-cols-3 gap-4">
          {[
            ["Plugins", stats?.plugins],
            ["Brands", stats?.brands],
            ["Direct links", stats?.with_direct_links],
          ].map(([label, val]) => (
            <div key={label} className="rounded-md border border-white/10 bg-[#141417] p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</div>
              <div data-testid={`stat-${label.toLowerCase().replace(" ", "-")}`} className="mt-1 font-heading text-3xl font-black">{val ?? "—"}</div>
            </div>
          ))}
        </div>

        <AnalyticsDashboard />
        <RegisteredMembers />

        <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <JobCard
            title="Expand Catalog"
            desc="AI generates ~300 essential famous plugins missing from the catalog, with direct manufacturer links (URL-validated)."
            job={jobs.expand}
            onRun={() => runJob("expand")}
            testId="run-expand-btn"
            Icon={Rocket}
          />
          <JobCard
            title="Resolve Direct Links"
            desc="Fixes wrong brands and adds official manufacturer buy links to existing plugins that only have Plugin Boutique."
            job={jobs.resolve}
            onRun={() => runJob("resolve-links")}
            testId="run-resolve-btn"
            Icon={Link2}
          />
          <JobCard
            title="Link Health Sweep"
            desc="Checks every buy link in the catalog, removes dead ones (incl. Plugin Boutique ghost pages), then AI re-resolves any plugin left without a link."
            job={jobs.link_health}
            onRun={() => runJob("link-health")}
            testId="run-link-health-btn"
            Icon={ShieldCheck}
          />
          <JobCard
            title="Fetch Artwork"
            desc="Visits each plugin's product page and pulls the manufacturer's official artwork for plugins missing images."
            job={jobs.images}
            onRun={() => runJob("fetch-images")}
            testId="run-fetch-images-btn"
            Icon={Image}
          />
        </div>

        <div className="mt-8 rounded-md border border-white/10 bg-[#141417] p-6">
          <div className="flex items-center gap-2 font-heading text-base font-bold">
            <Wand2 className="h-4 w-4 text-cyan-400" /> Magic Add
          </div>
          <p className="mt-1.5 text-sm text-white/50">Type plugin names (one per line, up to 20). The AI researches each, builds its knowledge profile and finds the official link. You review before it goes live.</p>
          <textarea
            data-testid="magic-add-input"
            value={names}
            onChange={(e) => setNames(e.target.value)}
            placeholder={"FabFilter Pro-Q 4\nValhalla Supermassive\nSoundtoys Decapitator"}
            rows={4}
            className="mt-4 w-full rounded-md border border-white/10 bg-black/40 p-3 font-mono text-sm text-white placeholder-white/25 outline-none focus:border-cyan-500/50"
          />
          <button
            data-testid="magic-preview-btn"
            disabled={busy}
            onClick={doPreview}
            className="mt-3 rounded-lg bg-cyan-500 px-5 py-2 text-sm font-semibold text-black transition-colors duration-200 hover:bg-cyan-400 disabled:opacity-40"
          >
            {busy && !preview ? "Researching..." : "Preview"}
          </button>

          {preview && (
            <div className="mt-5">
              <div className="overflow-hidden rounded-md border border-white/10">
                {preview.map((e, i) => (
                  <label key={i} data-testid={`preview-row-${i}`} className="flex cursor-pointer items-center gap-3 border-b border-white/5 px-4 py-3 last:border-0 hover:bg-white/5">
                    <input
                      type="checkbox"
                      checked={!!selected[i]}
                      onChange={() => setSelected((s) => ({ ...s, [i]: !s[i] }))}
                      className="accent-cyan-500"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-semibold">{e.doc.name}</span>
                      <span className="ml-2 text-xs text-white/40">{e.doc.brand}</span>
                      <span className="ml-2 rounded bg-white/5 px-1.5 py-0.5 font-mono text-[10px] uppercase text-cyan-300">{e.doc.structured_knowledge.plugin_type}</span>
                    </div>
                    <span className="font-mono text-xs text-white/50">{e.doc.current_price === 0 ? "FREE" : e.doc.current_price ? `$${e.doc.current_price}` : "—"}</span>
                    {e.url_ok ? (
                      <a
                        href={e.doc.vendor_urls[0]?.buy_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(ev) => ev.stopPropagation()}
                        className="flex items-center gap-1 font-mono text-[10px] text-emerald-400 hover:underline"
                      >
                        <CheckCircle2 className="h-3 w-3" /> LINK OK
                      </a>
                    ) : (
                      <span className="flex items-center gap-1 font-mono text-[10px] text-amber-400"><XCircle className="h-3 w-3" /> NO LINK</span>
                    )}
                  </label>
                ))}
              </div>
              <button
                data-testid="magic-import-btn"
                disabled={busy}
                onClick={doImport}
                className="mt-4 rounded-lg bg-emerald-500 px-5 py-2 text-sm font-semibold text-black transition-colors duration-200 hover:bg-emerald-400 disabled:opacity-40"
              >
                Import selected
              </button>
            </div>
          )}
        </div>

        <FeedbackInbox />
        <AdminSettings />
      </main>
    </div>
  );
}
